import * as React from "react";
import { cn } from "../lib/cn";

export type EmptyStateProps = {
  /** Emoji ou icône. Un visuel doux vaut mieux qu'un pictogramme technique. */
  glyph?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  /** `subtle` pour un vide attendu (journée libre), `card` pour un vide notable. */
  variant?: "subtle" | "card";
  className?: string;
};

/**
 * État vide.
 *
 * Règle produit : un vide n'est JAMAIS neutre, il a toujours une cause et
 * souvent une action. « Aucune donnée » est un aveu d'échec de conception.
 * Chaque état vide doit répondre à « pourquoi c'est vide » et « et maintenant ».
 *
 * Deuxième règle : distinguer le vide *heureux* du vide *problématique*.
 * Une inbox vide est une victoire, un catalogue vide est un blocage — les deux
 * ne se formulent pas pareil (cf. `emptyStates` plus bas).
 */
export function EmptyState({
  glyph,
  title,
  description,
  action,
  variant = "subtle",
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 py-12 text-center animate-fade-up",
        variant === "card" && "rounded-xl border border-dashed border-creme-300 bg-creme-50/60",
        className,
      )}
    >
      {glyph && (
        <div
          aria-hidden
          className="flex size-14 items-center justify-center rounded-full bg-blush-50 text-2xl"
        >
          {glyph}
        </div>
      )}
      <div className="space-y-1">
        <p className="font-display text-lg text-encre-900">{title}</p>
        {description && (
          <p className="mx-auto max-w-xs text-sm leading-relaxed text-encre-500">{description}</p>
        )}
      </div>
      {action && <div className="pt-1">{action}</div>}
    </div>
  );
}

/**
 * Catalogue des états vides du produit, rédigés une bonne fois.
 *
 * Les avoir ici plutôt que dispersés dans les écrans garantit un ton unique —
 * et rend impossible le « Aucun résultat » posé à la va-vite en fin de sprint.
 */
export const emptyStates = {
  /* --- Côté cliente ----------------------------------------------------- */
  noSlots: {
    glyph: "🗓️",
    title: "Complet sur cette période",
    description:
      "Prévenez-moi dès qu'un créneau se libère — la plupart partent dans les 24 h.",
  },
  noAppointments: {
    glyph: "💅",
    title: "Aucun rendez-vous à venir",
    description: "Vos ongles ont peut-être envie de nouveauté ?",
  },
  noPortfolioResults: {
    glyph: "🔍",
    title: "Rien avec ces filtres",
    description: "Essayez avec moins de critères, il y a sûrement mieux à voir.",
  },

  /* --- Côté équipe : vides heureux -------------------------------------- */
  inboxZero: {
    glyph: "🌸",
    title: "Tout est répondu",
    description: "Aucun message en attente. Profitez-en.",
  },
  noPending: {
    glyph: "✅",
    title: "Rien à confirmer",
    description: "Tous les rendez-vous demandés ont été traités.",
  },
  freeDay: {
    glyph: "☕",
    title: "Journée libre",
    description: "Aucun rendez-vous aujourd'hui.",
  },
  dayDone: {
    glyph: "🎉",
    title: "Journée terminée",
    description: "Plus rien au planning. Belle soirée !",
  },

  /* --- Côté équipe : vides à corriger ----------------------------------- */
  noServices: {
    glyph: "📖",
    title: "Votre carte est vide",
    description: "Ajoutez une première prestation pour ouvrir les réservations en ligne.",
  },
  noPortfolio: {
    glyph: "📸",
    title: "Aucune réalisation publiée",
    description:
      "Les photos sont ce qui déclenche les réservations — commencez par une pose dont vous êtes fière.",
  },
} as const satisfies Record<string, Omit<EmptyStateProps, "action" | "className" | "variant">>;
